import classNames from 'classnames';
import { CalendarEvent, CalendarStatusLevel } from '../types';

// Compact event label/bar rendered inside a date cell. Visual distinction:
// contracts (primary), B2B (purple), listings (teal), billing (grey).
const TYPE_STYLES: Record<string, string> = {
  CONTRACT_START: 'bg-primary-50/10 text-primary-50 border-primary-50/40',
  CONTRACT_END: 'bg-primary-50/5 text-primary-50 border-primary-50/20 border-dashed',
  B2B_CONTRACT_START: 'bg-purple-100 text-purple-800 border-purple-300',
  B2B_CONTRACT_END: 'bg-purple-50 text-purple-800 border-purple-200 border-dashed',
  LISTING_START: 'bg-teal-50 text-teal-800 border-teal-300',
  LISTING_END: 'bg-teal-50/60 text-teal-800 border-teal-200 border-dashed',
  BILLING_DATE: 'bg-grey-900 text-grey-400 border-grey-800',
};

const LEVEL_DOT: Record<CalendarStatusLevel, string> = {
  normal: 'hidden',
  dueSoon: 'bg-warning',
  warning: 'bg-warning-800',
  blocked: 'bg-danger',
  completed: 'bg-grey-500',
};

interface EventBarProps {
  event: CalendarEvent;
  statusLevel: CalendarStatusLevel;
  onClick: (event: CalendarEvent) => void;
  compact?: boolean;
}

export const EventBar = ({ event, statusLevel, onClick, compact = false }: EventBarProps) => {
  const isEnd = event.eventType.endsWith('_END');
  return (
    <button
      type="button"
      onClick={() => onClick(event)}
      title={event.title}
      data-cy={`calendar-event-${event.eventId}`}
      className={classNames(
        'w-full cursor-pointer truncate rounded border px-1.5 text-left transition-colors hover:shadow-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-50/40',
        compact ? 'py-px text-[11px]' : 'py-0.5 text-xs',
        TYPE_STYLES[event.eventType] ?? TYPE_STYLES.BILLING_DATE
      )}
    >
      <span
        className={classNames(
          'inline-block size-1.5 rounded-full mr-1 align-middle',
          LEVEL_DOT[statusLevel]
        )}
      />
      <span className="align-middle">
        {isEnd ? '◧ ' : '▸ '}
        {event.title}
      </span>
      {event.renewable && (
        <span className="ml-1 align-middle rounded bg-purple-200 px-1 text-[9px] font-semibold uppercase text-purple-900">
          Renewable
        </span>
      )}
    </button>
  );
};
